/* ==========================================================================
   YOPPY motion.js — shared GSAP motion library (B2C + B2B).
   --------------------------------------------------------------------------
   Load order in <head> (defer), BEFORE this file:
     <script defer src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js"></script>
     <script defer src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js"></script>
     <script defer src="assets/js/motion.js"></script>

   Exposes window.MotionLib with self-contained, named helpers. EVERY tween is
   gated by motionEnabled(): if the user prefers reduced motion OR GSAP failed
   to load, no animation runs and all content is shown in its final state.

   The page is expected to set <html class="js-motion"> optimistically (inline,
   to avoid FOUC). If motion turns out to be unavailable, this file removes that
   class so the CSS initial-hidden states never trap content.
   ========================================================================== */
(function () {
  "use strict";

  var REDUCED = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var hasGSAP = !!window.gsap;

  function motionEnabled() { return hasGSAP && !REDUCED; }

  // Clear the optimistic FOUC-guard class if we cannot animate, so any
  // .fade-up-init / .scale-in-init elements become visible immediately.
  if (!motionEnabled()) {
    document.documentElement.classList.remove("js-motion");
  }

  if (motionEnabled() && window.ScrollTrigger) {
    window.gsap.registerPlugin(window.ScrollTrigger);
  }

  function clearWillChange(targets) {
    (window.gsap ? window.gsap.utils.toArray(targets) : []).forEach(function (el) {
      el.style.willChange = "auto";
    });
  }

  /* ---------------------------------------------------------------------- */
  /*  initScrollReveal(selector, options)                                   */
  /*  Generic fade-up. options: { y, scale, duration, ease, start, stagger }*/
  /*  When stagger > 0, elements that share the viewport reveal together    */
  /*  (ScrollTrigger.batch) for a clean grid cascade.                       */
  /*                                                                        */
  /*  SAFETY NET: reveal-gated content must never DEPEND on a scroll event. */
  /*  Non-scrolling renderers (screenshot tools, hidden/background tabs,    */
  /*  link-preview bots) never fire ScrollTrigger, which left everything    */
  /*  below the fold at opacity 0 forever. Every element registered here    */
  /*  is force-revealed shortly after load if its trigger hasn't fired.     */
  /* ---------------------------------------------------------------------- */
  var REVEAL_SAFETY_MS = 3000;
  var pendingReveal = [];
  var safetyArmed = false;

  function revealAllPending() {
    if (!window.gsap) return;
    var still = pendingReveal.filter(function (el) {
      return parseFloat(window.gsap.getProperty(el, "opacity")) < 1;
    });
    pendingReveal = [];
    if (!still.length) return;
    if (document.hidden) {
      /* rAF is frozen in hidden tabs: snap to final state, no tween. */
      window.gsap.set(still, { autoAlpha: 1, y: 0, scale: 1 });
      clearWillChange(still);
    } else {
      window.gsap.to(still, {
        autoAlpha: 1, y: 0, scale: 1, duration: 0.4, ease: "power2.out",
        overwrite: true, onComplete: function () { clearWillChange(still); }
      });
    }
  }

  function armRevealSafety() {
    if (safetyArmed) return;
    safetyArmed = true;
    var fire = function () { window.setTimeout(revealAllPending, REVEAL_SAFETY_MS); };
    if (document.readyState === "complete") { fire(); }
    else { window.addEventListener("load", fire); }
  }

  function initScrollReveal(selector, options) {
    if (!motionEnabled()) return;
    var o = options || {};
    var y = o.y == null ? 40 : o.y;
    var scale = o.scale == null ? 1 : o.scale;
    var duration = o.duration == null ? 0.45 : o.duration; /* snappier reveals (~320–450ms) */
    var ease = o.ease || "power3.out";
    var start = o.start || "top 85%";
    var stagger = o.stagger || 0;

    var els = window.gsap.utils.toArray(selector);
    if (!els.length) return;

    var fromVars = { autoAlpha: 0, y: y };
    if (scale !== 1) fromVars.scale = scale;
    window.gsap.set(els, fromVars);
    pendingReveal = pendingReveal.concat(els);
    armRevealSafety();

    if (stagger > 0 && window.ScrollTrigger) {
      window.ScrollTrigger.batch(els, {
        start: start,
        once: true,
        onEnter: function (batch) {
          window.gsap.to(batch, {
            autoAlpha: 1, y: 0, scale: 1,
            duration: duration, ease: ease, stagger: stagger, overwrite: true,
            onComplete: function () { clearWillChange(batch); }
          });
        }
      });
    } else {
      els.forEach(function (el) {
        window.gsap.to(el, {
          autoAlpha: 1, y: 0, scale: 1, duration: duration, ease: ease,
          scrollTrigger: { trigger: el, start: start, once: true },
          onComplete: function () { el.style.willChange = "auto"; }
        });
      });
    }
  }

  /* ---------------------------------------------------------------------- */
  /*  initHeroEntrance(headlineSelector)                                     */
  /*  Letter-by-letter staggered reveal. Splits each .word (or the headline */
  /*  text itself) into per-character spans, preserving word wrapping.      */
  /* ---------------------------------------------------------------------- */
  function splitToChars(el) {
    var words = el.querySelectorAll(".word");
    var targets = words.length ? words : [el];
    var chars = [];
    targets.forEach(function (node) {
      var text = node.textContent;
      node.textContent = "";
      for (var i = 0; i < text.length; i++) {
        var ch = text.charAt(i);
        var span = document.createElement("span");
        span.className = "char";
        span.textContent = ch;
        if (ch === " ") span.style.whiteSpace = "pre";
        node.appendChild(span);
        chars.push(span);
      }
    });
    return chars;
  }

  function initHeroEntrance(headlineSelector) {
    var headline = typeof headlineSelector === "string"
      ? document.querySelector(headlineSelector)
      : headlineSelector;
    if (!headline) return;
    if (!motionEnabled()) return; // headline already visible (no init class)

    /* Arabic (and any Arabic-script text) must NOT be split into per-char
       inline-block spans: that breaks cursive ligature shaping. Fall back to
       animating the whole .word blocks with the same tween. */
    var isArabic = document.documentElement.lang === "ar" ||
      /[؀-ۿ]/.test(headline.textContent);
    var targets = isArabic
      ? (headline.querySelectorAll(".word").length ? headline.querySelectorAll(".word") : [headline])
      : splitToChars(headline);
    window.gsap.from(targets, {
      yPercent: isArabic ? 40 : 120,
      autoAlpha: 0,
      duration: 0.5,
      ease: "back.out(1.7)",
      stagger: isArabic ? 0.09 : 0.025,
      onComplete: function () { headline.style.willChange = "auto"; }
    });
    return targets;
  }

  /* ---------------------------------------------------------------------- */
  /*  initCounters(selector)                                                 */
  /*  Each element carries data-count-to (number). Counts up on scroll-in.  */
  /* ---------------------------------------------------------------------- */
  function initCounters(selector) {
    var els = (window.gsap ? window.gsap.utils.toArray(selector) : Array.prototype.slice.call(document.querySelectorAll(selector)));
    els.forEach(function (el) {
      var target = parseFloat(el.getAttribute("data-count-to")) || 0;
      var suffix = el.getAttribute("data-count-suffix") || "";

      if (!motionEnabled()) { el.textContent = target + suffix; return; }

      var proxy = { val: 0 };
      // Keep the real final value in the DOM until the count-up actually
      // starts, so the number never gets stuck at 0 when the scroll trigger
      // doesn't fire (background tab, search/social crawler, reduced-motion
      // edge, slow GSAP load, etc.). "0" only shows the instant it animates.
      window.gsap.to(proxy, {
        val: target,
        duration: 1.2,
        ease: "power1.out",
        scrollTrigger: { trigger: el, start: "top 90%", once: true },
        onStart: function () { el.textContent = "0" + suffix; },
        onUpdate: function () { el.textContent = Math.round(proxy.val) + suffix; },
        onComplete: function () { el.textContent = target + suffix; }
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  /*  initParallax(selector, speed)                                          */
  /*  Scrubbed vertical parallax. speed 0.4 ≈ element drifts up as you      */
  /*  scroll past its trigger section.                                       */
  /* ---------------------------------------------------------------------- */
  function initParallax(selector, speed, triggerSelector) {
    if (!motionEnabled() || !window.ScrollTrigger) return;
    var els = window.gsap.utils.toArray(selector);
    var amount = (speed == null ? 0.4 : speed) * 100;
    els.forEach(function (el) {
      var trigger = triggerSelector ? document.querySelector(triggerSelector) : el.closest("section") || el;
      window.gsap.to(el, {
        yPercent: -amount,
        ease: "none",
        scrollTrigger: {
          trigger: trigger,
          start: "top top",
          end: "bottom top",
          scrub: true
        }
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  /*  initTilt(selector)                                                     */
  /*  Premium 3D tilt on pointer move (transform-only). Falls flat on leave.*/
  /* ---------------------------------------------------------------------- */
  function initTilt(selector) {
    if (!motionEnabled()) return;
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) return; // skip touch
    var els = Array.prototype.slice.call(document.querySelectorAll(selector));
    var MAX = 8; // degrees

    els.forEach(function (el) {
      function onMove(e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        el.style.transition = "transform 0.1s ease";
        el.style.transform = "perspective(900px) rotateX(" + (-py * MAX).toFixed(2) + "deg) rotateY(" + (px * MAX).toFixed(2) + "deg)";
      }
      function onEnter() { el.style.willChange = "transform"; }
      function onLeave() {
        el.style.transition = "transform 0.4s ease";
        el.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg)";
        el.style.willChange = "auto";
      }
      el.addEventListener("pointerenter", onEnter);
      el.addEventListener("pointermove", onMove);
      el.addEventListener("pointerleave", onLeave);
    });
  }

  /* ---------------------------------------------------------------------- */
  /*  Public API                                                            */
  /* ---------------------------------------------------------------------- */
  window.MotionLib = {
    enabled: motionEnabled,
    initScrollReveal: initScrollReveal,
    initHeroEntrance: initHeroEntrance,
    initCounters: initCounters,
    initParallax: initParallax,
    initTilt: initTilt
  };
})();
